import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useContacts } from "../../context/ContactsContext";
import { useContactEnrichment } from "../../pages/UCPList/hooks/useContactEnrichment";

export const useUCPListPage = () => {
  const {
    contacts,
    contactsMeta,
    loadingContacts,
    error,
    isEmpty,
    selectedContact,
    setSelectedContact,
    fetchContacts,
    fetchContactDetails,
  } = useContacts();

  const [searchTerm, setSearchTermState] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(15);
  const [contactTypeFilter, setContactTypeFilterState] = useState("all");

  const { enrichments, loadingEnrichment } = useContactEnrichment(contacts);

  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    const filters =
      contactTypeFilter && contactTypeFilter !== "all"
        ? { party_type: contactTypeFilter }
        : null;

    searchTimeoutRef.current = setTimeout(() => {
    fetchContacts({
      page: currentPage,
      page_size: itemsPerPage,
      search: searchTerm || null,
      filters,
    });
    }, 0);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [currentPage, itemsPerPage, searchTerm, contactTypeFilter, fetchContacts]);

  const meta = useMemo(() => {
    if (!contactsMeta) return null;

    return {
      totalItems: contactsMeta.totalItems,
      totalPages: contactsMeta.totalPages,
      currentPage: contactsMeta.currentPage,
      perPage: contactsMeta.perPage,
      hasNext: contactsMeta.hasNext,
      hasPrevious: contactsMeta.hasPrevious,
    };
  }, [contactsMeta]);

  const enhancedContacts = useMemo(() => {
    return contacts.map((contact) => ({
      ...contact,
      city: contact.address?.addressCity || "",
      addressLine: contact.address?.addressLine || "",
      state: contact.address?.addressState || "",
      postalCode: contact.address?.addressPostalCode || "",
      uuid: contact.partyUuid,
      ...(enrichments[contact.partyUuid] || {}),
    }));
  }, [contacts, enrichments]);

  // TODO: Remove client-side filter once backend implements party_type filtering in /contact/list.
  // Backend receives the filter (filters: { party_type }) but currently ignores it.
  // This is a temporary simulation using the role from enrichment data.
  const filteredContacts = useMemo(() => {
    if (!contactTypeFilter || contactTypeFilter === "all") return enhancedContacts;
    return enhancedContacts.filter(
      (c) => c.role?.toLowerCase() === contactTypeFilter.toLowerCase()
    );
  }, [enhancedContacts, contactTypeFilter]);

  const handleSelectContact = async (contactUuid) => {
    if (selectedContact?.partyUuid === contactUuid) return;

    await fetchContactDetails(contactUuid);
  };

  const refreshContacts = useCallback(() => {
    const filters =
      contactTypeFilter && contactTypeFilter !== "all"
        ? { party_type: contactTypeFilter }
        : null;
    fetchContacts({
      page: currentPage,
      page_size: itemsPerPage,
      search: searchTerm || null,
      filters,
    });
  }, [currentPage, itemsPerPage, searchTerm, contactTypeFilter, fetchContacts]);

  const setSearchTerm = useCallback((value) => {
    setCurrentPage(1);
    setSearchTermState(value);
  }, []);

  const setContactTypeFilter = useCallback((value) => {
    setCurrentPage(1);
    setContactTypeFilterState(value);
  }, []);

  return {
    contacts: filteredContacts,
    meta,
    loading: loadingContacts,
    error,
    isEmpty,
    selectedContact,

    // Local UI state
    searchTerm,
    setSearchTerm,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    contactTypeFilter,
    setContactTypeFilter,

    loadingEnrichment,

    // Actions
    setSelectedContact,
    handleSelectContact,
    refreshContacts,
    fetchContactDetails,
  };
};
